import { Controller } from "@hotwired/stimulus"

// Manages table column visibility and order.
// Persists config to localStorage. Visibility is applied via injected CSS so
// dynamically-streamed rows are automatically covered. Ordering is applied by
// physically moving <th> elements and reordering cells in new rows via a
// MutationObserver on <tbody>.
export default class extends Controller {
  static targets = ["panel", "trigger", "list"]
  static values = { storageKey: { type: String, default: "siem_column_config_v1" } }

  #serverOrder = []  // column names in the server's original DOM order
  #config = []       // [{ name, hidden }] in current display order
  #observer = null
  #dragSrcIdx = null
  #boundOutside = null

  connect() {
    this.#serverOrder = this.#readHeaderNames()
    if (this.#serverOrder.length === 0) return

    this.#config = this.#loadConfig()
    this.#apply()
    this.#watchTable()

    this.#boundOutside = e => {
      if (!this.panelTarget.classList.contains("hidden") && !this.element.contains(e.target)) {
        this.#close()
      }
    }
    document.addEventListener("click", this.#boundOutside)
  }

  disconnect() {
    if (this.#boundOutside) document.removeEventListener("click", this.#boundOutside)
    this.#observer?.disconnect()
    document.getElementById("siem-col-style")?.remove()
  }

  toggle() {
    if (this.panelTarget.classList.contains("hidden")) {
      this.#renderList()
      this.panelTarget.classList.remove("hidden")
      this.triggerTarget.classList.add("active")
    } else {
      this.#close()
    }
  }

  resetDefaults() {
    this.#config = this.#serverOrder.map(name => ({ name, hidden: false }))
    this.#save()
    this.#apply()
    this.#renderList()
  }

  // ── Drag-and-drop ──────────────────────────────────────────────────────────

  dragstart(event) {
    this.#dragSrcIdx = parseInt(event.currentTarget.dataset.colIdx)
    event.dataTransfer.effectAllowed = "move"
  }

  dragover(event) {
    event.preventDefault()
    event.dataTransfer.dropEffect = "move"
    event.currentTarget.classList.add("siem-col-drag-over")
  }

  dragleave(event) {
    event.currentTarget.classList.remove("siem-col-drag-over")
  }

  drop(event) {
    event.preventDefault()
    event.currentTarget.classList.remove("siem-col-drag-over")
    const tgtIdx = parseInt(event.currentTarget.dataset.colIdx)
    if (this.#dragSrcIdx === null || tgtIdx === this.#dragSrcIdx) return

    const [moved] = this.#config.splice(this.#dragSrcIdx, 1)
    this.#config.splice(tgtIdx, 0, moved)
    this.#save()
    this.#apply()
    this.#renderList()
  }

  dragend() {
    this.listTarget?.querySelectorAll(".siem-col-drag-over")
      .forEach(el => el.classList.remove("siem-col-drag-over"))
    this.#dragSrcIdx = null
  }

  // ── Toggle visibility ──────────────────────────────────────────────────────

  toggleColumn(event) {
    const idx = parseInt(event.currentTarget.dataset.colIdx)
    this.#config[idx].hidden = !event.currentTarget.checked
    this.#save()
    this.#apply()
  }

  // ── Private ────────────────────────────────────────────────────────────────

  #close() {
    this.panelTarget.classList.add("hidden")
    this.triggerTarget.classList.remove("active")
  }

  #getTable() {
    return this.element.closest(".siem-results-col")?.querySelector("table.table")
  }

  #readHeaderNames() {
    return Array.from(this.#getTable()?.querySelectorAll("thead th:not(.siem-cb-col)") || [])
      .map(th => th.textContent.trim())
  }

  #loadConfig() {
    let saved = []
    try { saved = JSON.parse(localStorage.getItem(this.storageKeyValue) || "[]") } catch { /* ignore */ }

    const result = []
    saved.forEach(c => {
      if (this.#serverOrder.includes(c.name)) {
        result.push({ name: c.name, hidden: !!c.hidden })
      }
    })
    this.#serverOrder.forEach(name => {
      if (!result.find(c => c.name === name)) result.push({ name, hidden: false })
    })
    return result
  }

  #save() {
    localStorage.setItem(this.storageKeyValue, JSON.stringify(this.#config))
  }

  #apply() {
    this.#applyHeaderOrder()
    this.#applyCSS()
    this.#reorderExistingRows()
  }

  #applyHeaderOrder() {
    const table = this.#getTable()
    if (!table) return
    const theadRow = table.querySelector("thead tr")
    if (!theadRow) return

    const ths = Array.from(theadRow.querySelectorAll("th:not(.siem-cb-col)"))
    const thMap = new Map(ths.map(th => [th.textContent.trim(), th]))
    this.#config.forEach(col => {
      const th = thMap.get(col.name)
      if (th) theadRow.appendChild(th)
    })
    // .siem-cb-col th is never moved and stays pinned at the left
  }

  // Injects CSS to hide columns by their 1-based nth-child position.
  // Because <th> elements are already in display order at this point,
  // hidden nth positions match correctly for all rows including streamed ones.
  #applyCSS() {
    let styleEl = document.getElementById("siem-col-style")
    if (!styleEl) {
      styleEl = document.createElement("style")
      styleEl.id = "siem-col-style"
      document.head.appendChild(styleEl)
    }

    const table = this.#getTable()
    if (!table) { styleEl.textContent = ""; return }

    const ths = Array.from(table.querySelectorAll("thead th"))
    const rules = []
    ths.forEach((th, i) => {
      if (this.#config.find(c => c.name === th.textContent.trim())?.hidden) {
        const n = i + 1
        rules.push(`table.no-margins thead th:nth-child(${n}) { display: none !important; }`)
        rules.push(`table.no-margins tbody td:nth-child(${n}) { display: none !important; }`)
      }
    })
    styleEl.textContent = rules.join("\n")
  }

  #reorderExistingRows() {
    const tbody = this.#getTable()?.querySelector("tbody")
    if (!tbody) return
    Array.from(tbody.querySelectorAll("tr")).forEach(tr => this.#reorderRow(tr))
  }

  // Reorders a row's cells from server order to current display order.
  // Skips colspan rows (search spinner, export notice).
  // .siem-cb-col cells are pinned and never reordered.
  // Uses data-siem-col attributes to identify cells after the first reorder,
  // since subsequent calls find cells already in a non-server order.
  #reorderRow(tr) {
    const cells = Array.from(tr.cells)
    const movable = cells.filter(td => !td.classList.contains("siem-cb-col"))
    if (movable.length !== this.#serverOrder.length) return

    let cellMap
    if (movable[0]?.dataset.siemCol) {
      cellMap = new Map(movable.map(td => [td.dataset.siemCol, td]))
    } else {
      cellMap = new Map(this.#serverOrder.map((name, i) => [name, movable[i]]))
      this.#serverOrder.forEach((name, i) => { movable[i].dataset.siemCol = name })
    }

    this.#config.forEach(col => {
      const cell = cellMap.get(col.name)
      if (cell) tr.appendChild(cell)
    })
  }

  #watchTable() {
    const tbody = this.#getTable()?.querySelector("tbody")
    if (!tbody) return

    this.#observer = new MutationObserver(mutations => {
      mutations.forEach(m => {
        m.addedNodes.forEach(node => {
          if (node.nodeType === Node.ELEMENT_NODE && node.tagName === "TR") {
            this.#reorderRow(node)
          }
        })
      })
    })

    this.#observer.observe(tbody, { childList: true })
  }

  #renderList() {
    if (!this.hasListTarget) return
    this.listTarget.innerHTML = this.#config.map((col, i) => `
      <div class="siem-col-item" draggable="true"
           data-col-idx="${i}"
           data-action="dragstart->accounts--logs--column-manager#dragstart
                        dragover->accounts--logs--column-manager#dragover
                        dragleave->accounts--logs--column-manager#dragleave
                        drop->accounts--logs--column-manager#drop
                        dragend->accounts--logs--column-manager#dragend">
        <span class="siem-col-drag-handle" aria-hidden="true">⠿</span>
        <label class="siem-col-label">
          <input type="checkbox" class="siem-col-checkbox"
                 data-col-idx="${i}"
                 data-action="change->accounts--logs--column-manager#toggleColumn"
                 ${col.hidden ? "" : "checked"}>
          <span class="siem-col-name">${this.#esc(col.name || "(unnamed)")}</span>
        </label>
      </div>`).join("")
  }

  #esc(str) {
    return String(str).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
  }
}
